import { Account } from "models/Account";
import { Email } from "models/Email";
import { Person } from "models/Person";
import { Tenant } from "models/Tenant";
import { UuidNil } from "common/uuid";

export interface ComposerRecipient {
    key: string;
    name: string;
    email: string;
    accountId: string | null;
    accountName: string;
    personId?: string;
}

export interface ComposerAttachment {
    id: string;
    name: string;
    size: number;
    blob: Blob | null;
    status: "reading" | "ready" | "error";
    invoiceId?: string;
}

export interface ComposeContext {
    replyId: string | null;
    accountId: string | null;
    personId: string | null;
}

export interface ComposerDraft {
    to: ComposerRecipient | null;
    cc: ComposerRecipient[];
    toInput: string;
    ccInput: string;
    tenantId: string | null;
    accountId: string | null;
    subject: string;
    body: string;
    reference: string;
    replyToId: string | null;
    type: "email" | "whatsapp";
    attachments: ComposerAttachment[];
}

export const optionalId = (value?: string | null): string | null => {
    const id = value?.trim();
    return id && id !== UuidNil ? id : null;
};

export const emailKey = (value: string) => value.trim().toLowerCase();
export const isEmailAddress = (value: string) => /^[^\s<>@,;"\\]+@[^\s<>@,;"\\]+$/.test(value.trim());

export const recipient = (email: string, name = "", account?: Account, personId?: string): ComposerRecipient => ({
    key: `${account?.id ?? ""}:${emailKey(email)}`,
    name: name.trim(),
    email: email.trim(),
    accountId: optionalId(account?.id),
    accountName: account?.legal_name ?? "",
    personId,
});

export const parseRecipient = (value: string): ComposerRecipient | null => {
    const input = value.trim();
    if (/[\r\n]/.test(input)) return null;
    const named = input.match(/^(.*?)\s*<([^<>]+)>$/);
    const email = named ? named[2].trim() : input;
    if (!isEmailAddress(email)) return null;
    const name = named ? named[1].trim().replace(/^"(.*)"$/, "$1").replace(/\\(["\\])/g, "$1") : "";
    return recipient(email, name);
};

// A comma in a quoted name or mailbox must not split an existing CC header.
export const splitAddresses = (value: string): string[] => {
    const parts: string[] = [];
    let start = 0;
    let quoted = false;
    let escaped = false;
    let angleDepth = 0;
    for (let index = 0; index < value.length; index++) {
        const character = value[index];
        if (escaped) { escaped = false; continue; }
        if (quoted && character === "\\") { escaped = true; continue; }
        if (character === '"') quoted = !quoted;
        if (!quoted && character === "<") angleDepth++;
        if (!quoted && character === ">") angleDepth--;
        if (!quoted && angleDepth === 0 && (character === "," || character === ";")) {
            parts.push(value.slice(start, index).trim());
            start = index + 1;
        }
    }
    parts.push(value.slice(start).trim());
    return parts.filter(Boolean);
};

export const uniqueRecipients = (values: ComposerRecipient[], to?: ComposerRecipient | null): ComposerRecipient[] => {
    const seen = new Set(to ? [emailKey(to.email)] : []);
    return values.filter(value => {
        const key = emailKey(value.email);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export const linkedAccounts = (personId: string, accounts: Account[]) =>
    accounts.filter(account => (account.persons ?? []).some(link => link.person_id === personId));

export const buildContacts = (accounts: Account[], persons: Person[]): ComposerRecipient[] => {
    const contacts: ComposerRecipient[] = [];
    persons.forEach(person => {
        if (!isEmailAddress(person.email ?? "")) return;
        const name = [person.name, person.last_name].filter(Boolean).join(" ");
        const linked = linkedAccounts(person.id, accounts);
        if (linked.length === 0) contacts.push(recipient(person.email, name, undefined, person.id));
        linked.forEach(account => contacts.push(recipient(person.email, name, account, person.id)));
    });
    accounts.forEach(account => {
        if (isEmailAddress(account.email ?? "")) contacts.push(recipient(account.email, account.legal_name, account));
    });
    const seen = new Set<string>();
    return contacts.filter(value => {
        if (seen.has(value.key)) return false;
        seen.add(value.key);
        return true;
    }).sort((left, right) => (left.name || left.email).localeCompare(right.name || right.email));
};

export const emptyDraft = (): ComposerDraft => ({
    to: null, cc: [], toInput: "", ccInput: "", tenantId: null, accountId: null,
    subject: "", body: "", reference: "", replyToId: null, type: "email", attachments: [],
});

export const initializeDraft = (context: ComposeContext, accounts: Account[], persons: Person[], tenants: Tenant[], reply: Email | null): ComposerDraft => {
    const draft = emptyDraft();
    draft.tenantId = tenants.length === 1 ? optionalId(tenants[0].id) : null;
    if (reply) {
        const account = accounts.find(value => value.id === reply.customer);
        draft.to = recipient(reply.sender ?? "", reply.sender_name ?? "", account);
        draft.accountId = optionalId(reply.customer);
        draft.tenantId = optionalId(reply.tenant);
        draft.replyToId = reply.id;
        draft.reference = reply.reference ?? "";
        draft.subject = /^re:\s*/i.test(reply.subject ?? "") ? reply.subject : `RE: ${reply.subject ?? ""}`;
        draft.type = reply.type === "whatsapp" ? "whatsapp" : "email";
        draft.cc = uniqueRecipients(splitAddresses(reply.carbon_copy ?? "").map(value => parseRecipient(value) ?? recipient(value)), draft.to);
        return draft;
    }
    const person = persons.find(value => value.id === context.personId);
    const linked = person ? linkedAccounts(person.id, accounts) : [];
    const account = accounts.find(value => value.id === context.accountId) ?? (linked.length === 1 ? linked[0] : undefined);
    if (account) {
        draft.accountId = account.id;
        draft.tenantId = optionalId(account.tenant);
    }
    if (person?.email) draft.to = recipient(person.email, [person.name, person.last_name].filter(Boolean).join(" "), account, person.id);
    else if (account?.email) draft.to = recipient(account.email, account.legal_name, account);
    return draft;
};

export const resolvedRecipients = (draft: ComposerDraft) => {
    const to = draft.toInput.trim() ? parseRecipient(draft.toInput) : draft.to;
    const pendingCc = splitAddresses(draft.ccInput).map(parseRecipient);
    const cc = uniqueRecipients([...draft.cc, ...pendingCc.filter((value): value is ComposerRecipient => value !== null)], to);
    return { to, cc, invalidCc: pendingCc.some(value => !value) || cc.some(value => !isEmailAddress(value.email)) };
};

export const draftProblem = (draft: ComposerDraft): "recipientRequired" | "recipientInvalid" | "ccInvalid" | "tenantRequired" | "attachmentsReading" | "attachmentsFailed" | "messageRequired" | null => {
    if (!draft.tenantId) return "tenantRequired";
    if (draft.type === "whatsapp") return draft.body.trim() ? null : "messageRequired";
    const values = resolvedRecipients(draft);
    if (!values.to) return draft.toInput.trim() ? "recipientInvalid" : "recipientRequired";
    if (!isEmailAddress(values.to.email)) return "recipientInvalid";
    if (values.invalidCc) return "ccInvalid";
    if (draft.attachments.some(value => value.status === "reading")) return "attachmentsReading";
    if (draft.attachments.some(value => value.status === "error")) return "attachmentsFailed";
    return null;
};

export const formatAddress = (value: ComposerRecipient) => {
    const name = value.name.trim();
    return name ? `"${name.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}" <${value.email.trim()}>` : value.email.trim();
};

export const plainTextToHtml = (body: string) => body.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r\n|\r|\n/g, "<br />");

export const readBlob = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("attachment read failed"));
    reader.onerror = () => reject(reader.error ?? new Error("attachment read failed"));
    reader.onabort = () => reject(new Error("attachment read aborted"));
    reader.readAsDataURL(blob);
});

export const createEmailPayload = async (draft: ComposerDraft): Promise<Email> => {
    const values = resolvedRecipients(draft);
    const email = new Email();
    email.tenant = draft.tenantId!;
    if (draft.accountId) email.customer = draft.accountId;
    if (draft.replyToId) email.reply_to = draft.replyToId;
    email.type = draft.type;
    email.recipient = draft.type === "whatsapp" ? draft.to?.email ?? "" : formatAddress(values.to!);
    email.carbon_copy = draft.type === "whatsapp" ? "" : values.cc.map(formatAddress).join(", ");
    email.subject = draft.subject;
    email.body = draft.type === "whatsapp" ? draft.body.replace(/\r\n|\r|\n/g, "<br />") : plainTextToHtml(draft.body);
    email.reference = draft.reference;
    email.attachments = draft.type === "whatsapp" ? [] : await Promise.all(draft.attachments.map(async value => `${value.name}*${await readBlob(value.blob!)}`));
    return email;
};

export const errorMessage = (reason: unknown): string => {
    if (typeof reason === "string") return reason;
    const error = reason as { response?: { data?: { message?: string; error?: string } }; message?: string } | null;
    return error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? "";
};
